import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import {
    B2bLcStatus,
    B2bUtilization,
    BackToBackLc,
    CreateB2bUtilizationRequest,
    CreateBackToBackLcRequest,
    PageResponse,
    UpdateBackToBackLcRequest,
} from '../interfaces/commercial.interface';

export interface BackToBackLcListParams {
    page?: number;
    size?: number;
    status?: B2bLcStatus;
    masterLcId?: string;
}

/** Returns data only — components own triggerDownload/showApiError per house rules. */
export const BackToBackLcService = {
    /** Institution-wide register (BackToBackLcRegister page). */
    listByInstitution: async (institutionId: string, params: BackToBackLcListParams = {}): Promise<PageResponse<BackToBackLc>> => {
        const { page = 0, size = 20, status, masterLcId } = params;
        const query = new URLSearchParams({ page: String(page), size: String(size) });
        if (status) query.set('status', status);
        if (masterLcId) query.set('masterLcId', masterLcId);
        return (await axios.get(`${ENDPOINTS.BACK_TO_BACK_LC.LIST_BY_INSTITUTION(institutionId)}?${query.toString()}`)).data;
    },
    /** Master-scoped list (BackToBackLcPanel tab inside ExportLcDetail). */
    listByMaster: async (institutionId: string, lcId: string, page = 0, size = 20): Promise<PageResponse<BackToBackLc>> =>
        (await axios.get(`${ENDPOINTS.BACK_TO_BACK_LC.LIST_BY_MASTER(institutionId, lcId)}?page=${page}&size=${size}`)).data,
    getById: async (institutionId: string, lcId: string, b2bId: string): Promise<BackToBackLc> =>
        (await axios.get(ENDPOINTS.BACK_TO_BACK_LC.GET_BY_ID(institutionId, lcId, b2bId))).data,
    create: async (institutionId: string, lcId: string, dto: CreateBackToBackLcRequest): Promise<BackToBackLc> =>
        (await axios.post(ENDPOINTS.BACK_TO_BACK_LC.CREATE(institutionId, lcId), dto)).data,
    update: async (institutionId: string, lcId: string, b2bId: string, dto: UpdateBackToBackLcRequest): Promise<BackToBackLc> =>
        (await axios.put(ENDPOINTS.BACK_TO_BACK_LC.UPDATE(institutionId, lcId, b2bId), dto)).data,
    remove: async (institutionId: string, lcId: string, b2bId: string): Promise<void> => {
        await axios.delete(ENDPOINTS.BACK_TO_BACK_LC.DELETE(institutionId, lcId, b2bId));
    },

    // ---- utilizations -----------------------------------------------------------------------
    listUtilizations: async (
        institutionId: string, lcId: string, b2bId: string, page = 0, size = 20,
    ): Promise<PageResponse<B2bUtilization>> =>
        (await axios.get(`${ENDPOINTS.BACK_TO_BACK_LC.UTILIZATIONS(institutionId, lcId, b2bId)}?page=${page}&size=${size}`)).data,
    createUtilization: async (
        institutionId: string, lcId: string, b2bId: string, dto: CreateB2bUtilizationRequest,
    ): Promise<B2bUtilization> =>
        (await axios.post(ENDPOINTS.BACK_TO_BACK_LC.UTILIZATIONS(institutionId, lcId, b2bId), dto)).data,
    updateUtilization: async (
        institutionId: string, lcId: string, b2bId: string, utilId: string, dto: CreateB2bUtilizationRequest,
    ): Promise<B2bUtilization> =>
        (await axios.put(ENDPOINTS.BACK_TO_BACK_LC.UTILIZATION_ITEM(institutionId, lcId, b2bId, utilId), dto)).data,
    removeUtilization: async (institutionId: string, lcId: string, b2bId: string, utilId: string): Promise<void> => {
        await axios.delete(ENDPOINTS.BACK_TO_BACK_LC.UTILIZATION_ITEM(institutionId, lcId, b2bId, utilId));
    },
};
